import { useNavigate } from 'react-router'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { DASH } from '@/ui/format'
import type { Candidate } from './user/teamPick'

// 사람 한 줄의 표. 사용자 목록의 등급 구획, 직위 상세의 사람, 팀의 멤버가 같은 칸을 쓴다 —
// 같은 사람이 화면마다 다른 칸으로 서면 어느 화면에서 본 값이 무엇이었는지 다시 맞춰 봐야 한다.
// 앞의 넷(사용자·이름·이메일·직위)은 늘 같고, 끝 칸(`last`)만 화면이 정한다 — 사용자 목록은 팀, 팀 멤버는 역할이다.

/** 끝 칸. 폭 없는 열이 이것 하나라 남는 폭을 가져간다. */
export type LastColumn = { head: string; cell: (p: Candidate) => string }

/**
 * 고르기는 사용자 목록만 쓴다. `selectable` 은 열이 있는가(보는 사람이 최고 관리자인가)이고 `pickable` 은 그 줄을 고를 수 있는가다.
 * 둘을 가르지 않으면 탈퇴 구획만 열이 하나 적어 다른 구획과 어긋난다.
 */
export type Picking = {
  selectable: boolean
  pickable: boolean
  picked: string[]
  onPick: (id: string, on: boolean) => void
  /** 머리의 체크박스 — 이 표에 보이는 줄을 통째로 고르고 푼다. */
  onPickAll?: (on: boolean) => void
  allLabel?: string
}

export function PeopleTable({
  group,
  last,
  opens,
  picking,
}: {
  group: Candidate[]
  last: LastColumn
  opens: (p: Candidate) => boolean
  picking?: Picking
}) {
  const selectable = picking?.selectable ?? false
  // 머리의 체크박스는 **이 구획에 보이는 줄**을 통째로 고르고 푼다 — 거른 뒤 누르면 남은 사람만 골라진다.
  const all = picking ? group.every((p) => picking.picked.includes(p.user.id)) : false
  return (
    <div className="overflow-hidden rounded-lg border bg-card">
      {/* 표가 auto 이면 머리의 폭은 최소 폭일 뿐이라 한 구획의 긴 이메일이 그 표의 열만 민다 — fixed 라야 내용이 폭을 못 바꾼다.
          좁은 화면에서는 표가 block 으로 접혀 table-layout 이 뜻이 없다. */}
      <Table className="table-fixed">
        <TableHeader>
          <TableRow className="hover:bg-transparent">
            {/* 구획마다 표가 따로 서므로 폭 없는 열을 하나만 둔다 — 그 하나가 남는 폭을 가져가야 세 표의 열이 같은 자리에 선다.
                폭은 머리에만 있다: 좁은 화면에서 머리가 접힐 때 폭도 함께 사라져야 한다. 넘치는 값은 칸 안에서 자르고 title 로 전체를 보인다. */}
            {selectable ? (
              <TableHead className="w-10 max-sm:hidden">
                {picking?.pickable && picking.onPickAll ? (
                  <Checkbox
                    aria-label={picking.allLabel ?? '전체 선택'}
                    checked={all}
                    onCheckedChange={(on) => picking.onPickAll?.(on === true)}
                  />
                ) : null}
              </TableHead>
            ) : null}
            {/* 폭이 박힌 열의 합이 열이 펼쳐지는 지점(sm, 640px)을 넘으면 남는 폭을 가져갈 끝 칸이 0 으로 눌리고 표가 가로로 스크롤된다 —
                직위 칸을 더하면서 이름과 이메일을 같은 만큼 좁혀 그 합을 640px 아래로 되돌렸다. 긴 이메일은 칸 안에서 자르고 title 이 전체를 보인다. */}
            <TableHead className="w-32">사용자</TableHead>
            <TableHead className="w-28 max-sm:hidden">이름</TableHead>
            <TableHead className="w-44 max-sm:hidden">이메일</TableHead>
            {/* 직위는 머리가 없으면 팀 이름과 구별되지 않고 상세의 `쿼터` 절에 이미 있으므로 좁은 화면에서 접힌다. */}
            <TableHead className="w-24 max-sm:hidden">직위</TableHead>
            <TableHead className="max-sm:hidden">{last.head}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {group.map((p) => (
            <Row key={p.user.id} person={p} last={last} opens={opens(p)} picking={picking} />
          ))}
        </TableBody>
      </Table>
    </div>
  )
}

function Row({ person: p, last, opens, picking }: { person: Candidate; last: LastColumn; opens: boolean; picking?: Picking | undefined }) {
  const nav = useNavigate()
  const lastText = last.cell(p)
  return (
    <TableRow
      onClick={opens ? () => nav(`/users/${p.user.id}`) : undefined}
      className={opens ? 'cursor-pointer hover:bg-surface-2' : 'hover:bg-transparent'}
    >
      {picking?.selectable ? (
        <TableCell className="max-sm:hidden" onClick={(e) => e.stopPropagation()}>
          {picking.pickable ? (
            <Checkbox
              aria-label={`${p.user.username} 선택`}
              checked={picking.picked.includes(p.user.id)}
              onCheckedChange={(on) => picking.onPick(p.user.id, on === true)}
            />
          ) : null}
        </TableCell>
      ) : null}
      {/* 자름은 sm 부터다 — 좁은 화면의 아이디 칸은 접힌 줄을 다 쓰고 줄바꿈한다(STACK). */}
      <TableCell className="font-medium max-sm:basis-full">
        <span className="sm:flex sm:min-w-0 sm:items-center">
          <span className="sm:truncate" title={p.user.username}>{p.user.username}</span>
          {p.user.withdrawn ? <span className="ml-1.5 shrink-0 rounded border px-1 text-[10px] text-muted-foreground">탈퇴</span> : null}
        </span>
      </TableCell>
      <TableCell className="text-muted-foreground max-sm:hidden">
        <span className="block truncate" title={p.user.name ?? undefined}>{p.user.name ?? DASH}</span>
      </TableCell>
      <TableCell className="text-muted-foreground max-sm:hidden">
        <span className="flex min-w-0 items-center">
          <span className="truncate" title={p.user.email ?? undefined}>{p.user.email ?? DASH}</span>
          {p.user.email && !p.user.email_verified ? <span className="ml-1.5 shrink-0 rounded border border-warn/35 px-1 text-[10px] text-warn-ink">미인증</span> : null}
        </span>
      </TableCell>
      <TableCell className="text-muted-foreground max-sm:hidden">
        <span className="block truncate" title={p.user.quota_tier?.display_name}>{p.user.quota_tier?.display_name ?? DASH}</span>
      </TableCell>
      <TableCell className="text-muted-foreground max-sm:hidden">
        <span className="block truncate" title={lastText || undefined}>{lastText || DASH}</span>
      </TableCell>
    </TableRow>
  )
}
